import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, CalendarIcon, AlertTriangle, CheckCircle2, XCircle, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdminLayout from '../../components/AdminLayout';

const API_URL = window.location.origin;

const AdminCredits = () => {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [reviewingCredit, setReviewingCredit] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCredits();
  }, [dateFrom, dateTo]);

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
      if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
      const { data } = await axios.get(`${API_URL}/api/admin/credits`, { withCredentials: true, params });
      setCredits(data);
    } catch (e) {
      toast.error('Failed to load credits');
    } finally {
      setLoading(false);
    }
  };

  const filteredCredits = useMemo(() => {
    if (filterStatus === 'all') return credits;
    return credits.filter(c => c.status === filterStatus);
  }, [credits, filterStatus]);

  const stats = useMemo(() => {
    const pending = credits.filter(c => c.status === 'pending').length;
    const approved = credits.filter(c => c.status === 'approved').length;
    const disapproved = credits.filter(c => c.status === 'disapproved').length;
    return { total: credits.length, pending, approved, disapproved };
  }, [credits]);

  const openReview = (credit) => {
    setReviewingCredit(credit);
    setReviewStatus(credit.status);
    setAdminComment(credit.admin_comment || '');
  };

  const handleReview = async () => {
    if (!reviewingCredit) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/credits/${reviewingCredit.order_id}/${reviewingCredit.item_index}`,
        { status: reviewStatus, admin_comment: adminComment },
        { withCredentials: true }
      );
      toast.success(`Credit ${reviewStatus}`);
      setReviewingCredit(null);
      fetchCredits();
    } catch (e) {
      toast.error('Failed to update credit');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return { className: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Approved' };
      case 'disapproved': return { className: 'bg-red-100 text-red-700', icon: XCircle, label: 'Disapproved' };
      default: return { className: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' };
    }
  };

  if (loading && credits.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Credits & Disputes</h1>
          <p className="text-sm text-slate-500 mt-1">Review and manage item reports raised by clients</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-slate-200/60">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-5 h-5 text-slate-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Reports</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardContent className="p-4 text-center">
              <Clock className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
              <p className="text-xs text-yellow-600">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
              <p className="text-xs text-green-600">Approved</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4 text-center">
              <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-red-600">{stats.disapproved}</p>
              <p className="text-xs text-red-500">Disapproved</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start rounded-xl h-10 w-full sm:w-[170px]" data-testid="credits-date-from">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
                {dateFrom ? format(dateFrom, 'dd MMM yyyy') : <span className="text-slate-400">From</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start rounded-xl h-10 w-full sm:w-[170px]" data-testid="credits-date-to">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400 flex-shrink-0" />
                {dateTo ? format(dateTo, 'dd MMM yyyy') : <span className="text-slate-400">To</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
            </PopoverContent>
          </Popover>
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="flex-1 sm:w-[160px] rounded-xl" data-testid="credits-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="disapproved">Disapproved</SelectItem>
              </SelectContent>
            </Select>
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={() => { setDateFrom(null); setDateTo(null); }}
                className="text-xs text-slate-500 self-center h-10 px-2">Clear</Button>
            )}
          </div>
        </div>

        {/* Credits Table */}
        <Card className="border-slate-200/60 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Delivery Week</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Client Notes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Admin Comment</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCredits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        No credits or disputes found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCredits.map((credit, idx) => {
                      const sb = getStatusBadge(credit.status);
                      const StatusIcon = sb.icon;
                      let weekLabel;
                      try { weekLabel = format(new Date(credit.delivery_week), 'dd MMM yyyy'); } catch { weekLabel = credit.delivery_week; }
                      return (
                        <TableRow key={`${credit.order_id}-${credit.item_index}-${idx}`} data-testid={`credit-row-${idx}`}>
                          <TableCell className="font-medium">{weekLabel}</TableCell>
                          <TableCell>{credit.organisation_name}</TableCell>
                          <TableCell className="text-sm text-slate-500">{credit.user_username}</TableCell>
                          <TableCell className="font-medium">{credit.product_name}</TableCell>
                          <TableCell>
                            <Badge className={credit.report_type === 'missing' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}>
                              {credit.report_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{credit.notes || '-'}</TableCell>
                          <TableCell>
                            <Badge className={sb.className}>
                              <StatusIcon className="w-3 h-3 mr-1" />{sb.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{credit.admin_comment || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openReview(credit)}
                              className="text-green-700 hover:text-green-800 hover:bg-green-50 rounded-full"
                              data-testid={`review-credit-${idx}`}>
                              <MessageSquare className="w-4 h-4 mr-1" />Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Review Dialog */}
        <Dialog open={!!reviewingCredit} onOpenChange={() => setReviewingCredit(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Review Credit / Dispute</DialogTitle>
            </DialogHeader>
            {reviewingCredit && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Product:</span>
                    <span className="font-medium">{reviewingCredit.product_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Issue:</span>
                    <Badge className={reviewingCredit.report_type === 'missing' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}>
                      {reviewingCredit.report_type}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Organisation:</span>
                    <span className="font-medium">{reviewingCredit.organisation_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Client:</span>
                    <span>{reviewingCredit.user_username}</span>
                  </div>
                  {reviewingCredit.notes && (
                    <div>
                      <span className="text-slate-600">Client Notes:</span>
                      <p className="mt-1 text-slate-800 bg-white rounded-lg p-2 border">{reviewingCredit.notes}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Decision</label>
                  <Select value={reviewStatus} onValueChange={setReviewStatus}>
                    <SelectTrigger className="rounded-xl" data-testid="review-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approve Credit</SelectItem>
                      <SelectItem value="disapproved">Disapprove Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Admin Comment</label>
                  <Textarea
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    placeholder="Add a comment for team review..."
                    className="rounded-xl"
                    rows={3}
                    data-testid="admin-comment-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewingCredit(null)} className="rounded-full">Cancel</Button>
              <Button onClick={handleReview} disabled={submitting}
                className="rounded-full bg-green-600 hover:bg-green-700" data-testid="submit-review-button">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Review'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCredits;
